import { yupResolver } from '@hookform/resolvers'
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents'
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader'
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import {
  Grid,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { ErrorDialog } from 'components/Dialog/ErrorDialog'
import { ExitConfirmationDialog } from 'components/Dialog/ExitConfirmationDialog'
import { AccountFooter } from 'components/Footer/AccountFooter'
import { TooltipAmountWithContractSum } from 'components/Footer/TooltipAmountWithContractSum'
import SnackBarContext from 'containers/App/Store/SnackBarContext'
import {
  ApprovalStatus,
  useCreateScheduledBillingMutation,
  useGetBillItemFromDepartmentLazyQuery,
  useGetCostCentreCodeQuery,
  useGetDebtorAccountNameQuery,
  useGetDebtorAccountQuery,
  useGetUnusedSalesOrderByDebtorLazyQuery,
  useUpdateArScheduledBillingMutation,
} from 'generated/graphql'
import { useMenuOption } from 'helpers/Hooks/useMenuOption'
import { usePermissionChecker } from 'helpers/Hooks/usePermissionChecker'
import { SystemMsgs } from 'helpers/Messages/SystemMsg'
import { amtStr } from 'helpers/StringNumberFunction/NumFormatters'
import { CommonYupValidation } from 'helpers/YupSchema/yup'
import React, { Fragment, useContext, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import * as yup from 'yup'
import { ScheduledBillingItemDetail } from './ScheduledBillingItemDetail'
import { ARScheduledBillingItemComponent } from './ScheduledBillingItemDialog/ScheduledBillingItemDialog'

interface ScheduledBillingProps {
  DebtorAccountID: string
  CompanyID: string
  SalesOrderID: string
  BillingDate: Date
  BillingAmt: number
  BillItemID: string
  TaxSchemeID: string
  CostCentreID: string
  Description: string
  Remark: string
}

export const ARScheduledBillingForm = (props: any) => {
  let history = useHistory()
  let location = useLocation()
  const editData: any = location?.state
  const { formMode } = props
  const { CompanyID, ScheduledBillingSubmenu }: any = useParams()
  const user = JSON.parse(localStorage.getItem('loggedInUser'))
  const { ScheduledBillingID }: any = useParams()
  const {
    anchorEl,
    menu,
    handleClick,
    handleClose,
    resetMenu,
  } = useMenuOption()

  let form, mode
  switch (formMode) {
    case 'add':
      form = 'New'
      mode = 'add'
      break
    case 'edit':
      form = 'Draft'
      mode = 'edit'
      break
  }

  const { handlePermDisabled } = usePermissionChecker()

  /* ------------------------------------------- */
  /*                   STATE                     */
  /* ------------------------------------------- */
  const [unitPrice, setUnitPrice] = useState(editData?.BillingAmt ?? 0)
  const [openExitConf, setOpenExitConf] = useState(null)
  const { setOpenSnackBar, setSnackBarMsg } = useContext(SnackBarContext) as any
  const [errMessage, setErrMessage] = useState(null)
  const [headerErrMessage, setHeaderErrMessage] = useState(null)
  const [errDialog, setErrDialog] = useState(false)
  const [itemData, setItemData] = useState([])
  const [openItemDialog, setOpenItemDialog] = useState<boolean>(false)
  const [edit, setEdit] = useState(false)

  /* -------------------------------------------- */
  /*                   USE FORM                   */
  /* -------------------------------------------- */
  const ScheduledBillingSchema = yup.object().shape({
    DebtorAccountID: CommonYupValidation.requireField(SystemMsgs.debtorName()),
    SalesOrderID: CommonYupValidation.requireField(SystemMsgs.salesOrder()),
    CostCentreID: CommonYupValidation.requireField(SystemMsgs.department()),
  })

  const { handleSubmit, register, setValue, control, errors, watch } = useForm<
    ScheduledBillingProps
  >({
    defaultValues: {},
    mode: 'onSubmit',
    resolver: yupResolver(ScheduledBillingSchema),
  })

  /* -------------------------------------------- */
  /*                   MUTATION                   */
  /* -------------------------------------------- */
  const [
    createARScheduledBilling,
    { loading: createScheduledBillingLoading },
  ] = useCreateScheduledBillingMutation({
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    onCompleted: () => {
      history.push(
        `/account-receivable/${CompanyID}/scheduled-billing/sales-order`
      )
      setSnackBarMsg(SystemMsgs.createNewRecord())
      setOpenSnackBar(true)
    },
  })

  const [
    updateARScheduledBilling,
    { loading: updateARScheduledBillingLoading },
  ] = useUpdateArScheduledBillingMutation({
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    onCompleted: () => {
      history.push(
        `/account-receivable/${CompanyID}/scheduled-billing/sales-order`
      )
      setSnackBarMsg(SystemMsgs.updateRecord())
      setOpenSnackBar(true)
    },
  })

  /* -------------------------------------------- */
  /*                 FUNCTION                     */
  /* -------------------------------------------- */
  const onDeleteScheduledBillingItem = index => {
    itemData.splice(index, 1)
  }

  /* -------------------------------------------- */
  /*                    QUERY                     */
  /* -------------------------------------------- */
  const {
    loading: DepartmentLoading,
    data: { getCostCentre } = {
      getCostCentre: [],
    },
  } = useGetCostCentreCodeQuery({
    fetchPolicy: 'network-only',
    variables: { CompanyID, IsLastNode: true },
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    onCompleted: () => {
      if (formMode === 'edit' && editData?.ScheduledBillingItem?.length > 0) {
        const items = editData?.ScheduledBillingItem?.map((el, index) => {
          return {
            ScheduledBillingItemID: el?.ScheduledBillingItemID,
            BillingDate: el?.ScheduledBillingDate,
            BillingAmt: el?.BillingAmt,
            BillItemID: el?.BillItemID,
            BillItemName: el?.BillItem?.Name,
            isBilled: el?.isBilled,
            BillDate: el?.BillDate,
            RefRecordID: el?.RefRecordID ?? null,
            RefTable: el?.RefTable ?? null,
            createdTs: el?.createdTs ?? null,
            createdBy: el?.createdBy ?? null,
            TaxSchemeID: el?.TaxSchemeID,
            TaxCode: el?.TaxScheme?.Name,
            TaxRate: 0,
            TaxAmt: el?.TaxAmt,
            DocAmt: el?.DocAmt,
            Description: el?.Description,
            Remark: el?.Remark,
            ItemTaxInfo: el?.ItemTaxInfo,
            DocNo: el?.Invoice?.DocNo,
            InvoiceDocAmt: el?.Invoice?.DocAmt ?? null,
            InvoiceAllocations: el?.Invoice?.Allocation ?? [],
          }
        }).sort(
          (a, b) =>
            new Date(b.BillingDate || b.BillDate).getTime() -
            new Date(a.BillingDate || a.BillDate).getTime()
        )
        itemData.push(...items)
      }
    },
  })

  // const [
  //   fetchBillItem,
  //   { loading: BillItemLoading, data: { getBillItem } = { getBillItem: [] } },
  // ] = useGetBillItemListingLazyQuery({
  //   fetchPolicy: 'network-only',
  //   variables: {
  //     orderByAsc: 'Name',
  //     RecordStatus: RecordStatus.Active,
  //   },
  //   onError: ({ message }) => {
  //     let error = message?.substring(15)
  //     setErrMessage(error)
  //     setErrDialog(true)
  //   },
  // })

  const [
    fetchFilteredBillItem,
    {
      loading: FilteredBillItemLoading,
      data: { getBillItemFromDepartment } = { getBillItemFromDepartment: [] },
    },
  ] = useGetBillItemFromDepartmentLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
  })

  const {
    loading: DebtorAccountLoading,
    data: { getDebtorAccountListing } = { getDebtorAccountListing: [] },
  } = useGetDebtorAccountNameQuery({
    fetchPolicy: 'network-only',
    variables: { CompanyID },
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    onCompleted: () => {
      if (editData) {
        fetchSalesOrder({
          variables: {
            DebtorAccountID: editData?.DebtorAccountID,
            excludeSalesOrderID: editData?.SalesOrderID ?? null,
            CompanyID: CompanyID,
          },
        })
        setValue('SalesOrderID', editData?.SalesOrderID)
        setValue('CostCentreID', editData?.CostCentreID)
      }
    },
  })

  const [
    fetchSalesOrder,
    {
      loading: SalesOrderLoading,
      data: { getUnusedSalesOrderByDebtor } = {
        getUnusedSalesOrderByDebtor: [],
      },
    },
  ] = useGetUnusedSalesOrderByDebtorLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
  })

  /* -------------------------------------------- */
  /*                    FOOTER                    */
  /* -------------------------------------------- */
  //Footer checking for Contract Sum
  const salesOrderID = editData?.SalesOrderID || watch('SalesOrderID')
  const selectedOrder = getUnusedSalesOrderByDebtor?.find(
    order => order?.id === salesOrderID
  )
  const getEffectiveBillingAmt = (item: any): number => {
    const billingAmt = Number(item.BillingAmt) || 0
    if (!item.isBilled || !item.InvoiceAllocations?.length) {
      return billingAmt
    }

    const cnAllocatedAmt = item.InvoiceAllocations.filter(
      (a: any) => a.CreditRefTable === 'AR_CreditNote'
    ).reduce((sum: number, a: any) => sum + (Number(a.AllocationAmt) || 0), 0)

    if (cnAllocatedAmt <= 0) return billingAmt

    const invoiceDocAmt = Number(item.InvoiceDocAmt) || Number(item.DocAmt) || 0
    if (invoiceDocAmt <= 0) return billingAmt

    return Math.max(0, billingAmt * (1 - cnAllocatedAmt / invoiceDocAmt))
  }

  const checkAmountMismatch = () => {
    const totalEffectiveAmtCents = itemData.reduce(
      (sum, item) => sum + Math.round(getEffectiveBillingAmt(item) * 100),
      0
    )
    if (!selectedOrder) {
      return true
    }
    const orderAmount = parseFloat(
      selectedOrder.revisedOrderSum && selectedOrder.revisedOrderSum !== 0
        ? selectedOrder.revisedOrderSum
        : selectedOrder.orderSum
    )
    const orderAmountCents = Math.round(orderAmount * 100)

    return totalEffectiveAmtCents !== orderAmountCents
  }

  const draftFooterOption = {
    name: 'Save as Draft',
    onClick: () => {
      handleSubmit(data => onSubmit(data, 'draft'))()
    },
    color: 'primary',
    props: { type: 'submit' },
    // disabled: handlePermDisabled({
    //   companyID: CompanyID,
    //   permEnum: AcctPermission.AccReceivableSalesOrderBillingScheduleCreate,
    // })
    //   ? true
    //   : false,
  }

  const submitFooterOption = {
    name: 'Complete',
    onClick: () => {
      handleSubmit(data => onSubmit(data, 'completed'))()
    },
    color: 'primary',
    props: { type: 'submit' },
    disabled: checkAmountMismatch(),
    // ||
    // handlePermDisabled({
    //   companyID: CompanyID,
    //   permEnum: AcctPermission.AccReceivableSalesOrderBillingScheduleCreate,
    // })
    //   ? true
    //   : false,
  }

  const footerOptions: any[] = [
    ...(editData?.ApprovalStatus !== 'COMPLETED' ? [draftFooterOption] : []),
    submitFooterOption,
  ]

  /* -------------------------------------------- */
  /*                   ON SUBMIT                  */
  /* -------------------------------------------- */
  const onSubmit = (data, status) => {
    // const todayDateString = formatDashDate(new Date().toISOString())
    // const hasInvalidDate = itemData?.some(x => {
    //   if (x?.BillingDate) {
    //     const billingDateString = formatDashDate(
    //       new Date(x?.BillingDate)?.toISOString()
    //     )
    //     return billingDateString < todayDateString
    //   }
    //   return false
    // })
    // if (hasInvalidDate) {
    //   setErrMessage("Billing date cannot be less than today's date")
    //   setErrDialog(true)
    //   return
    // }
    if (formMode === 'add') {
      createARScheduledBilling({
        variables: {
          input: {
            CompanyID,
            DebtorAccountID: data?.DebtorAccountID,
            SalesOrderID: data?.SalesOrderID,
            CostCentreID: data?.CostCentreID,
            ScheduledBillingItems: itemData?.map(x => {
              const itemTaxInput =
                x?.ItemTaxInfo && x.ItemTaxInfo.length > 0
                  ? x.ItemTaxInfo.map(tax => ({
                      tax_amt: tax.taxAmt,
                      tax_rate: tax.taxRate,
                      tax_id: tax.taxId,
                      tax_scheme_detail_id: tax.taxSchemeDetailId,
                      tax_scheme_id: tax.taxSchemeId,
                    }))
                  : []

              return {
                ScheduledBillingDate: x?.BillingDate,
                BillingAmt: Number(x?.DocAmt) - Number(x?.TaxAmt),
                BillItemID: x?.BillItemID,
                TaxSchemeID: x?.TaxSchemeID,
                TaxRate: 0,
                TaxAmt: Number(x?.TaxAmt),
                DocAmt: Number(x?.DocAmt),
                Description: x?.Description,
                Remark: x?.Remark,
                itemTaxInput: itemTaxInput,
              }
            }),
            ApprovalStatus:
              status === 'completed'
                ? ApprovalStatus.Completed
                : ApprovalStatus.Active,
          },
        },
      })
    } else {
      updateARScheduledBilling({
        variables: {
          ScheduledBillingID,
          input: {
            CompanyID,
            DebtorAccountID: data?.DebtorAccountID,
            SalesOrderID: data?.SalesOrderID,
            CostCentreID: data?.CostCentreID,
            ScheduledBillingItems: itemData?.map(x => {
              const itemTaxInput =
                x?.ItemTaxInfo && x.ItemTaxInfo.length > 0
                  ? x.ItemTaxInfo.map(tax => ({
                      tax_amt: tax.taxAmt,
                      tax_rate: tax.taxRate,
                      tax_id: tax.taxId,
                      tax_scheme_detail_id: tax.taxSchemeDetailId,
                      tax_scheme_id: tax.taxSchemeId,
                    }))
                  : []
              return {
                ScheduledBillingItemID: x?.ScheduledBillingItemID,
                ScheduledBillingDate: x?.BillingDate,
                BillingAmt: Number(x?.DocAmt) - Number(x?.TaxAmt),
                BillItemID: x?.BillItemID,
                isBilled: x?.isBilled ?? false,
                BillDate: x?.BillDate ?? null,
                RefRecordID: x?.RefRecordID ?? null,
                RefTable: x?.RefTable ?? null,
                createdTs: x?.createdTs ?? null,
                createdBy: x?.createdBy ?? null,
                TaxSchemeID: x?.TaxSchemeID,
                TaxRate: 0,
                TaxAmt: Number(x?.TaxAmt),
                DocAmt: Number(x?.DocAmt),
                Description: x?.Description,
                Remark: x?.Remark,
                itemTaxInput: itemTaxInput,
              }
            }),
            ApprovalStatus:
              status === 'completed'
                ? ApprovalStatus.Completed
                : ApprovalStatus.Active,
          },
        },
      })
    }
  }

  return (
    <>
      {/* {BillItemLoading && <Loading />} */}
      {DepartmentLoading && <Loading />}
      {SalesOrderLoading && <Loading />}
      {DebtorAccountLoading && <Loading />}
      {FilteredBillItemLoading && <Loading />}
      {createScheduledBillingLoading && <Loading />}
      {updateARScheduledBillingLoading && <Loading />}
      <MainHeader
        mainBtn="close"
        onClick={() => {
          history.push(
            `/account-receivable/${CompanyID}/scheduled-billing/sales-order`
          )
        }}
        smTitle={'Account Receivable'}
        title={user?.companyName}
        routeSegments={[
          { name: 'Main Menu' },
          { name: 'Account Receivable' },
          { name: 'Schedule Billing' },
          { name: 'Sales Order Billing Schedule', current: true },
        ]}
        rightRouteSegments={[
          {
            name:
              formMode === 'add'
                ? 'New'
                : formMode === 'approve-reject'
                ? 'Approve/Reject'
                : 'Edit',
            current: true,
          },
        ]}
      />

      <ContentWrapper float footer>
        <CardContents>
          {!DebtorAccountLoading && (
            <Controller
              render={({ value, onChange }) => {
                const defVal = getDebtorAccountListing?.filter(
                  coa => coa?.DebtorAccountID === editData?.DebtorAccountID
                )[0]
                return (
                  <Autocomplete
                    ref={register}
                    options={
                      getDebtorAccountListing?.sort((a, b) => {
                        return a?.DebtorName?.localeCompare(b?.DebtorName)
                      }) || []
                    }
                    getOptionLabel={option => {
                      return `${option?.DebtorName}${
                        option?.einvoice_TIN
                          ? ` | TIN: ${option?.einvoice_TIN}`
                          : ''
                      }`
                    }}
                    fullWidth
                    onChange={(value, newValue: any) => {
                      setValue('DebtorAccountID', newValue?.DebtorAccountID)
                      fetchSalesOrder({
                        variables: {
                          DebtorAccountID: newValue?.DebtorAccountID ?? null,
                          excludeSalesOrderID: editData?.SalesOrderID ?? null,
                          CompanyID: CompanyID,
                        },
                      })
                    }}
                    defaultValue={defVal}
                    disabled={editData?.ApprovalStatus === 'COMPLETED'}
                    renderOption={(props, option) => {
                      return (
                        <div>
                          <div>
                            <span className="xsTitle">
                              {props?.DebtorName}
                              {props?.einvoice_TIN &&
                                ` (TIN: ${props.einvoice_TIN})`}
                            </span>
                          </div>
                        </div>
                      )
                    }}
                    renderInput={(params: any) => {
                      return (
                        <div>
                          <TextField
                            {...params}
                            helperText={errors?.DebtorAccountID?.message}
                            error={errors?.DebtorAccountID ? true : false}
                            label="Debtor Name"
                            style={{
                              width: '100%',
                            }}
                            required
                            margin="normal"
                          />
                        </div>
                      )
                    }}
                  />
                )
              }}
              label="Debtor Name"
              name="DebtorAccountID"
              autoComplete="off"
              control={control}
              multiline={true}
              fullWidth
              margin="normal"
              ref={register}
              required
              helperText={errors?.DebtorAccountID?.message}
              error={errors?.DebtorAccountID ? true : false}
              defaultValue={editData ? editData?.DebtorAccountID : ''}
            />
          )}
          {!SalesOrderLoading && (
            <Controller
              render={({ value, onChange }) => {
                const defVal = getUnusedSalesOrderByDebtor?.filter(
                  coa => coa?.id === editData?.SalesOrderID
                )[0]

                return (
                  <Autocomplete
                    options={getUnusedSalesOrderByDebtor || []}
                    getOptionLabel={option => {
                      const revised = option?.revisedOrderSum
                      const fallback = option?.orderSum
                      const amount = revised
                        ? amtStr(revised)
                        : amtStr(fallback)
                      return `${option?.title || 'N/A'} | ${amount || 'N/A'}`
                    }}
                    fullWidth
                    onChange={(value, newValue: any) => {
                      setValue('SalesOrderID', newValue?.id)
                    }}
                    defaultValue={defVal}
                    disabled={editData?.ApprovalStatus === 'COMPLETED'}
                    renderOption={(props, option) => (
                      <Fragment>
                        <ListItemText
                          primary={
                            <Grid container>
                              <Grid
                                item
                                xs={8}
                                component="span"
                                className="xsTitle "
                              >
                                {props?.title || 'N/A'}
                              </Grid>
                              <Grid
                                item
                                xs={4}
                                component="span"
                                className="xsTitle right-text highlight-text"
                              >
                                {amtStr(
                                  props?.revisedOrderSum ?? props?.orderSum
                                )}
                              </Grid>
                            </Grid>
                          }
                        />
                      </Fragment>
                    )}
                    renderInput={(params: any) => {
                      return (
                        <div>
                          <TextField
                            {...params}
                            helperText={errors?.SalesOrderID?.message}
                            error={errors?.SalesOrderID ? true : false}
                            label="Sales Order"
                            style={{ width: '100%' }}
                            required
                            margin="normal"
                            defaultValue={defVal}
                          />
                        </div>
                      )
                    }}
                  />
                )
              }}
              label="Sales Order"
              name="SalesOrderID"
              autoComplete="off"
              control={control}
              multiline={true}
              fullWidth
              margin="normal"
              ref={register}
              required
              helperText={errors?.SalesOrderID?.message}
              error={errors?.SalesOrderID ? true : false}
              defaultValue={
                !!watch('DebtorAccountID') ? editData?.SalesOrderID : undefined
              }
            />
          )}
          <Controller
            render={({ value, onChange }) => {
              const defVal = !!watch('CostCentreID')
                ? getCostCentre?.find(
                    x => x?.CostCentreID === watch('CostCentreID')
                  )
                : null
              return (
                <Autocomplete
                  options={getCostCentre || []}
                  getOptionLabel={option =>
                    option ? `${option?.Code} | ${option?.Name}` : ''
                  }
                  fullWidth
                  onChange={(value, newValue: any, reason) => {
                    onChange(newValue?.CostCentreID)
                    setItemData([])
                    fetchFilteredBillItem({
                      variables: {
                        CompanyID,
                        CostCentreID: newValue?.CostCentreID,
                      },
                    })
                  }}
                  renderOption={(props, option) => {
                    return (
                      <div>
                        <div>
                          <span className="xsTitle">{props?.Code}</span>
                        </div>
                        <div className="desc">{props?.Name}</div>
                      </div>
                    )
                  }}
                  value={defVal}
                  disabled={editData?.ApprovalStatus === 'COMPLETED'}
                  renderInput={(params: any) => {
                    return (
                      <div>
                        <TextField
                          {...params}
                          helperText={errors?.CostCentreID?.message}
                          error={errors?.CostCentreID ? true : false}
                          label="Department"
                          style={{
                            width: '100%',
                          }}
                          margin="dense"
                          required
                        />
                      </div>
                    )
                  }}
                />
              )
            }}
            label="Department"
            name="CostCentreID"
            autoComplete="off"
            control={control}
            multiline={true}
            fullWidth
            margin="dense"
            ref={register}
            helperText={errors?.CostCentreID?.message}
            error={errors?.CostCentreID ? true : false}
            required
          />
        </CardContents>

        <ScheduledBillingItemDetail
          itemData={itemData}
          setOpenDialog={setOpenItemDialog}
          resetMenu={resetMenu}
          handleClick={handleClick}
          keyItemID={'ScheduledBillingItemID'}
          fetchFilteredBillItem={fetchFilteredBillItem}
          watch={watch}
          editData={editData}
          CompanyID={CompanyID}
        />
      </ContentWrapper>

      {!FilteredBillItemLoading && (
        <ARScheduledBillingItemComponent
          openItemDialog={openItemDialog}
          setOpenItemDialog={setOpenItemDialog}
          menu={menu}
          BillItemLoading={FilteredBillItemLoading}
          fetchFilteredBillItem={fetchFilteredBillItem}
          CompanyID={CompanyID}
          CostCentreID={watch('CostCentreID')}
          getBillItem={getBillItemFromDepartment}
          isEdit={edit}
          itemData={itemData}
        />
      )}

      {footerOptions?.length > 0 && (
        <AccountFooter options={[...footerOptions]} />
      )}

      <Menu
        id="menu-list"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={handleClose}
      >
        <MenuItem
          onClick={() => {
            fetchFilteredBillItem({
              variables: {
                CompanyID,
                CostCentreID: editData?.CostCentreID ?? watch('CostCentreID'),
              },
            })
            setEdit(true)
            setOpenItemDialog(true)
          }}
        >
          <span>Edit</span>
        </MenuItem>
        <MenuItem
          onClick={() => {
            onDeleteScheduledBillingItem(menu?.index)
          }}
        >
          <span>Delete</span>
        </MenuItem>
      </Menu>

      <ErrorDialog
        errorDia={errDialog}
        setErrorDia={setErrDialog}
        errorMsg={errMessage}
        errorHeaderMsg={headerErrMessage ?? 'Error!'}
      />

      <ExitConfirmationDialog
        openExitConf={openExitConf}
        setOpenExitConf={setOpenExitConf}
        onConfirm={() => {
          history.push(`/account-receivable/${CompanyID}/scheduled-billing`)
        }}
      />
      <TooltipAmountWithContractSum
        data={itemData}
        ContractSum={selectedOrder?.revisedOrderSum ?? selectedOrder?.orderSum}
        module={'scheduled-billing'}
      />
    </>
  )
}
